// @flow
import React from 'react';
import Dotdotdot from 'react-dotdotdot';
import { DateUtil } from 'utils/DateUtil';
import { CallToAction } from 'components/atoms/callToAction';
import { useViewport } from 'components/templates/viewportProvider';
import type { EventDate, Venue } from 'types/event';
import { isEmpty } from 'lodash';
import { CURRENT_COUNTRY_CODE } from 'config/env';
import { CountryTimeUtil } from 'utils/CountryTimeUtil';
// eslint-disable-next-line
import styles from "./styles.scss";

type Props = {
  data: {
    title: string,
    subTitle?: string,
    imageUrl: string,
    venue?: Venue,
  },
  onClick: () => void,
  from: string,
};

export const EventCardMobile = (props: Props) => {
  const { height } = useViewport();
  const { data, from, ctaText } = props;

  if (!data) {
    return null;
  }

  const { venue } = data;
  const eventDates = venue?.eventDates || [];
  const countryCode = CURRENT_COUNTRY_CODE === '+1' ? 'USA' : 'INDIA';
  const validEventDates = CountryTimeUtil.filterValidEvents(eventDates, countryCode, false);

  const sortDates =
    from === 'upcoming' && !isEmpty(validEventDates)
      ? validEventDates.sort(function(a, b) {
          return new Date(a.date) - new Date(b.date);
        })
      : {};
  const firstEventDate = Array.isArray(sortDates) && sortDates.length > 0 ? sortDates[0] : undefined;
  const allEventDates = Array.isArray(sortDates) && sortDates.length > 0 ? sortDates.map((eventDate) => eventDate?.date) : [];
  const displayDates =
    from === 'upcoming'
      ? (Array.isArray(allEventDates) ? allEventDates : []).map((date) => DateUtil.getShortDisplayDateFromISOString(date))
      : [];

  function getDisplayEventDate(eventDates) {
    if (!Array.isArray(eventDates) || eventDates.length === 0) return ''
    if (eventDates.length === 1) return eventDates[0];

    let groups = [];
    let currentGroup = {
      month: '',
      days: []
    };

    eventDates.forEach(date => {
      const [month, day] = date.split(' ');
      if (currentGroup.month === '') {
        currentGroup.month = month;
        currentGroup.days.push(day);
      } else if (currentGroup.month === month) {
        currentGroup.days.push(day);
      } else {
        groups.push(currentGroup);
        currentGroup = {
          month: month,
          days: [day]
        };
      }
    });
    groups.push(currentGroup);

    const formattedGroups = (Array.isArray(groups) ? groups : []).map(group => {
      if (group.days.length === 1) {
        return `${group.month} ${group.days[0]}`;
      } else if( group.days.length === 2) {
        return `${group.month} ${group.days.join(' & ')}`;
      } else {
        const allButLast = group.days.slice(0, -1).join(', ');
        const last = group.days[group.days.length - 1];
        return `${group.month} ${allButLast} & ${last}`;
      }
    })

    if (formattedGroups.length === 1) {
      return formattedGroups[0];
    } else if (formattedGroups.length === 2) {
      return formattedGroups.join(' & ');
    } else {
      const lastGroup = formattedGroups.pop();
      return formattedGroups.join(', ') + ' & ' + lastGroup;
    }
  }

  return (
    <div className={'event-card-mobile'} style={{ height }}>
      <div className={'content'}>
        <h1>{getDisplayEventDate(displayDates)}</h1>
        <img src={data.imageUrl} alt={'Event Image'} />
        <div className={'event-info'}>
          <div className={'event-header'}>
            <Dotdotdot clamp={2} tagName={'h2'}>
              {data.title}
            </Dotdotdot>
            <span>{data.subTitle}</span>
          </div>
          <div className={'event-content'}>
            {from === 'upcoming' && (
              <p>
                {firstEventDate?.startTime}{' '}
                {firstEventDate?.endTime ? `to ${firstEventDate?.endTime}` : 'onwards'}{' '}
              </p>
            )}
            {venue && (
              <>
                <p>{venue.name}</p>
                <p>{venue.location}</p>
              </>
            )}
          </div>
          <CallToAction
            text={ctaText}
            onClick={props.onClick}
            mode={'transparent'}
            type={'small'}
            isMobile={true}
          />
        </div>
      </div>
    </div>
  );
};
